'use client';

import Link from '@eliadmin/core/src/components/link';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eliadmin/core/src/components/ui/tooltip';
import { FaCheck } from 'react-icons/fa';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import type { ProductAccessoryVariantRow } from '../product-accessory-variants-container';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export function ProductAccessoryDesktopTable({
  variants,
}: {
  variants: ProductAccessoryVariantRow[];
}) {
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const { t } = useTranslations();

  const sortedVariants = variants.map(variant => ({
    ...variant,
    attributes: Array.isArray(variant.attributes)
      ? [...variant.attributes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      : [],
  }));

  const attributeColumns = sortedVariants
    .flatMap(variant => variant.attributes ?? [])
    .reduce<
      {
        id: number;
        name: string;
        position: number;
      }[]
    >((acc, attribute) => {
      if (acc.some(item => item.id === attribute.id)) {
        return acc;
      }

      acc.push({
        id: attribute.id,
        name: attribute.name,
        position: attribute.position ?? 0,
      });

      return acc;
    }, [])
    .sort((a, b) => a.position - b.position);

  const attributeGridStyle: CSSProperties | undefined = attributeColumns.length
    ? {
        gridTemplateColumns: attributeColumns.map(() => 'minmax(88px, 120px)').join(' '),
      }
    : undefined;

  const middleScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement | null>(null);
  const scrollbarDragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const [scrollbarMetrics, setScrollbarMetrics] = useState({
    hasOverflow: false,
    thumbOffset: 0,
    thumbWidth: 0,
  });

  useEffect(() => {
    const updateScrollbarMetrics = () => {
      const viewport = middleScrollRef.current;
      const track = scrollbarTrackRef.current;

      if (!viewport || !track) {
        return;
      }

      const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
      const hasOverflow = maxScrollLeft > 0;
      const trackWidth = track.clientWidth;

      if (!hasOverflow || trackWidth <= 0) {
        setScrollbarMetrics({
          hasOverflow,
          thumbOffset: 0,
          thumbWidth: trackWidth,
        });
        return;
      }

      const thumbWidth = Math.max((viewport.clientWidth / viewport.scrollWidth) * trackWidth, 24);
      const maxThumbOffset = Math.max(trackWidth - thumbWidth, 0);
      const thumbOffset =
        maxScrollLeft > 0 ? (viewport.scrollLeft / maxScrollLeft) * maxThumbOffset : 0;

      setScrollbarMetrics({
        hasOverflow: true,
        thumbOffset,
        thumbWidth,
      });
    };

    updateScrollbarMetrics();

    const viewport = middleScrollRef.current;
    if (!viewport) {
      return;
    }

    viewport.addEventListener('scroll', updateScrollbarMetrics, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      updateScrollbarMetrics();
    });

    resizeObserver.observe(viewport);

    if (scrollbarTrackRef.current) {
      resizeObserver.observe(scrollbarTrackRef.current);
    }

    window.addEventListener('resize', updateScrollbarMetrics);

    return () => {
      viewport.removeEventListener('scroll', updateScrollbarMetrics);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollbarMetrics);
    };
  }, [attributeColumns.length, sortedVariants.length]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const dragState = scrollbarDragRef.current;
      const viewport = middleScrollRef.current;
      const track = scrollbarTrackRef.current;

      if (!dragState || !viewport || !track || scrollbarMetrics.thumbWidth <= 0) {
        return;
      }

      const maxTrackOffset = Math.max(track.clientWidth - scrollbarMetrics.thumbWidth, 0);
      const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);

      if (maxTrackOffset <= 0 || maxScrollLeft <= 0) {
        return;
      }

      const dragDistance = event.clientX - dragState.startX;
      const nextScrollLeft =
        dragState.startScrollLeft + (dragDistance / maxTrackOffset) * maxScrollLeft;

      viewport.scrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScrollLeft);
    };

    const handleMouseUp = () => {
      scrollbarDragRef.current = null;
      document.body.style.removeProperty('user-select');
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [scrollbarMetrics.thumbWidth]);

  const handleScrollbarTrackMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    const viewport = middleScrollRef.current;
    const track = scrollbarTrackRef.current;

    if (!viewport || !track || !scrollbarMetrics.hasOverflow) {
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const maxTrackOffset = Math.max(trackRect.width - scrollbarMetrics.thumbWidth, 0);
    const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);

    if (maxTrackOffset <= 0 || maxScrollLeft <= 0) {
      return;
    }

    const nextThumbOffset = Math.min(
      Math.max(event.clientX - trackRect.left - scrollbarMetrics.thumbWidth / 2, 0),
      maxTrackOffset,
    );

    viewport.scrollLeft = (nextThumbOffset / maxTrackOffset) * maxScrollLeft;
  };

  const handleScrollbarThumbMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    const viewport = middleScrollRef.current;

    if (!viewport || !scrollbarMetrics.hasOverflow) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    scrollbarDragRef.current = {
      startScrollLeft: viewport.scrollLeft,
      startX: event.clientX,
    };

    document.body.style.userSelect = 'none';
  };

  return (
    <div className="hidden w-full sm:flex sm:flex-col">
      <div className="w-full overflow-hidden rounded-[18px] border border-[#E9E9E9] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
        <div className="flex w-full items-stretch">
          <div className="w-[240px] shrink-0 border-r border-r-[#E9E9E9]">
            <div className="flex h-16 items-center bg-[#F8FAFC] px-3">
              <span className="text-balance text-[13px] font-medium text-foreground">
                {t('text552')}
              </span>
            </div>
            <TooltipProvider delayDuration={150}>
              {sortedVariants.map((variant, variantIndex) => {
                const isLastVariantRow = variantIndex === sortedVariants.length - 1;
                const nameCellClassName = cn(
                  'flex h-[70px] items-center px-3 text-primary',
                  'border-t border-t-[#E9E9E9]',
                  isLastVariantRow && 'shadow-[inset_0_-1px_0_0_#E9E9E9]',
                  variant.isCurrentProduct && 'bg-primary/5',
                  variant.isCurrentProduct && 'font-bold',
                );

                const nameTextClassName = 'line-clamp-3 w-full leading-[1.1] uppercase underline';
                const variantName = variant.name || variant.code;

                const nameCell = variant.isCurrentProduct ? (
                  <div className={cn(nameCellClassName, 'cursor-default')}>
                    <span className={nameTextClassName}>{variantName}</span>
                  </div>
                ) : (
                  <Link
                    href={`/${variant.slug}`}
                    prefetch={true}
                    replace
                    className={cn(nameCellClassName, 'cursor-pointer')}
                  >
                    <span className={nameTextClassName}>{variantName}</span>
                  </Link>
                );

                return (
                  <Tooltip key={variant.id}>
                    <TooltipTrigger asChild>{nameCell}</TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="start"
                      className="max-w-[320px] break-words text-xs font-medium uppercase leading-[1.3]"
                    >
                      {variantName}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>

          <div className="min-w-0 flex-1">
            <div
              ref={middleScrollRef}
              className="hide-scroll-bar overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_0_#E9E9E9]"
            >
              <div className={cn(attributeColumns.length ? 'min-w-max' : 'min-w-0')}>
                <div
                  className={cn(
                    'h-16 items-stretch bg-[#F8FAFC]',
                    attributeColumns.length ? 'grid' : 'hidden',
                  )}
                  style={attributeGridStyle}
                >
                  {attributeColumns.map(attribute => (
                    <div
                      key={attribute.id}
                      className="flex h-16 items-center border-l border-l-[#E9E9E9] px-2 py-3 first:border-l-0"
                    >
                      <span className="line-clamp-2 text-[13px] font-medium text-foreground">
                        {attribute.name}
                      </span>
                    </div>
                  ))}
                </div>

                {sortedVariants.map((variant, variantIndex) => {
                  const isLastVariantRow = variantIndex === sortedVariants.length - 1;
                  const attributeValueMap = new Map(
                    (variant.attributes ?? []).map(attribute => [
                      attribute.id,
                      attribute.values
                        ?.map(value => value.name)
                        .filter(Boolean)
                        .join(', ') ?? '',
                    ]),
                  );

                  return (
                    <div
                      key={`attributes-${variant.id}`}
                      className={cn(
                        'h-[70px] items-stretch border-t border-t-[#E9E9E9]',
                        variant.isCurrentProduct && 'bg-primary/5',
                        attributeColumns.length ? 'grid' : 'hidden',
                      )}
                      style={attributeGridStyle}
                    >
                      {attributeColumns.map(attribute => (
                        <div
                          key={`${variant.id}-${attribute.id}`}
                          className={cn(
                            'flex h-[70px] items-center border-l border-l-[#E9E9E9] px-2 py-3 text-sm text-foreground first:border-l-0',
                            isLastVariantRow && 'shadow-[inset_0_-1px_0_0_#E9E9E9]',
                          )}
                        >
                          <span className="line-clamp-2 break-words">
                            {attributeValueMap.get(attribute.id) || '--'}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="w-[280px] shrink-0 border-l border-l-[#E9E9E9] shadow-[inset_0_-1px_0_0_#E9E9E9]">
            <div className="grid h-16 grid-cols-[90px_190px] bg-[#F8FAFC]">
              <div className="flex h-16 items-center px-3">
                <span className="text-balance text-[13px] font-medium text-foreground">
                  {t('text164')}
                </span>
              </div>
              <div className="flex h-16 items-center border-l border-l-[#E9E9E9] px-3">
                <span className="text-balance text-[13px] font-medium text-foreground">
                  {t('text3605')}
                </span>
              </div>
            </div>

            {sortedVariants.map(variant => {
              const priceDisplay = getProductPriceDisplay(variant, { isWholesaler });
              return (
                <div
                  key={`summary-${variant.id}`}
                  className={cn(
                    'grid h-[70px] grid-cols-[90px_190px] border-t border-t-[#E9E9E9] bg-white',
                    variant.isCurrentProduct && 'bg-primary/5',
                  )}
                >
                  <div className="flex h-[70px] items-center px-3 text-sm text-foreground">
                    {priceDisplay.priceOnRequest
                      ? t('text533')
                      : formatPrice(priceDisplay.currentWithVat, currency, currencySettings)}
                  </div>

                  <div className="flex h-[70px] items-center border-l border-l-[#E9E9E9] px-3">
                    <div className="flex items-center justify-start gap-2.5">
                      <div
                        className={cn(
                          'flex min-h-5 min-w-5 items-center justify-center rounded-full',
                          variant.availability.id === 1 ? 'bg-[#57AE00]' : 'bg-[#FF0000]',
                        )}
                      >
                        <FaCheck size={12} className="text-white" />
                      </div>
                      <p
                        className={cn(
                          'text-sm font-bold',
                          variant.availability.id === 1 ? 'text-[#57ae00]' : 'text-[#ff0000]',
                        )}
                      >
                        {variant.availability.name}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center py-[3px]">
          <div className="w-[240px] shrink-0" />
          <div className="min-w-0 flex-1 px-2">
            <div
              ref={scrollbarTrackRef}
              className={cn(
                'relative h-[4px] w-full rounded-full bg-[#E9E9E9]',
                !scrollbarMetrics.hasOverflow && 'opacity-0',
              )}
              onMouseDown={handleScrollbarTrackMouseDown}
            >
              <div
                className="absolute top-0 h-[4px] cursor-pointer rounded-full bg-[#C7CDD8]"
                style={{
                  transform: `translateX(${scrollbarMetrics.thumbOffset}px)`,
                  width: `${scrollbarMetrics.thumbWidth}px`,
                }}
                onMouseDown={handleScrollbarThumbMouseDown}
              />
            </div>
          </div>
          <div className="w-[280px] shrink-0" />
        </div>
      </div>
    </div>
  );
}
